import { BehaviorSubject } from 'rxjs';
import { Themes } from '@ids/tokens';
import { platform } from '../platform';

/**
 * Todos os segmentos disponiveis como typo.
 *
 * @category Segment
 */
export type SegmentTypes = keyof typeof Themes;

/**
 * Todos os valores de segmentos disponiveis como typo.
 *
 * @category Segment
 */
export type SegmentValueTypes = Lowercase<SegmentTypes>;

/**
 * Objeto contendo todos os segmentos.
 *
 * @category Segment
 */
export const Segments: Record<SegmentTypes, SegmentValueTypes> = Object.keys(Themes).reduce(
  (segments, token) => ({ ...segments, [token as SegmentTypes]: token.toLocaleLowerCase() }),
  {} as Record<SegmentTypes, SegmentValueTypes>,
);

const _segment = new BehaviorSubject<SegmentTypes>('Varejo');

const _elementWithSegment = new BehaviorSubject<HTMLElement | undefined>(
  /* istanbul ignore next */
  platform.isDOM ? document.documentElement : undefined,
);

// TO-DO: manter comentado e substituir pela cdn

// const _segmentTokens = new BehaviorSubject<{ [key in string]: string }>(Varejo);

const getKeySegment = (value?: SegmentValueTypes): SegmentTypes =>
  Object.keys(Segments).find(
    (key: string) => Segments[key as SegmentTypes] === value,
  ) as SegmentTypes;

/**
 * Todos os possíveis modos de cor.
 *
 * @category Segment
 */
export type SegmentColorModeTypes = 'light' | 'dark';

/**
 * Objeto contendo todos os modos de cor.
 *
 * @category Segment
 */
export const SegmentColorModes: Record<SegmentColorModeTypes, SegmentColorModeTypes> = {
  light: 'light',
  dark: 'dark',
};

/**
 * Retorna o modo de cor atual.
 *
 * @category Segment
 *
 * @returns `light` | `dark`
 */
export const getCurrentColorMode = (): SegmentColorModeTypes | undefined =>
  /* istanbul ignore next */
  _elementWithSegment.value?.getAttribute('data-theme') as SegmentColorModeTypes;

/**
 * Observable do nome do Segmento.
 *
 * @category Segment
 */
export const Segment = _segment.asObservable();

// TO-DO: mater comentado e substituir pela cdn

// /**
//  * Observable de tokens por segmento.
//  *
//  * @category Segment
//  */
// export const SegmentTokens = _segmentTokens.asObservable();

/**
 *
 * @param segment Novo valor de Segmento
 * @param element elemento DOM para aplicar a classe
 * @param colorMode Modo de cor
 *
 * @category Segment
 *
 * @example
 * ```ts
 * import { applySegmentToElement, Segments, SegmentColorModes } from '@ids/tools';
 *
 * changeSegment(Segments.Ion, document.body, SegmentColorModes.dark);
 */
export const applySegmentToElement = (
  segment: SegmentValueTypes = Segments.Varejo!,
  elementToApplySegment: HTMLElement | Document | string = document.documentElement,
  colorMode?: SegmentColorModeTypes,
) => {
  const classes: string[] = [];

  let element: HTMLElement =
    elementToApplySegment instanceof HTMLElement ? elementToApplySegment : document.documentElement;

  if (typeof elementToApplySegment === 'string') {
    element = document.querySelector(elementToApplySegment) ?? element;
  }

  element.classList.forEach((className) => {
    if (className.startsWith('voxel-theme') || className.startsWith('ids-theme')) {
      classes.push(className);
    }
  });

  if (classes.length > 0) {
    element.classList.remove(...classes);
  }

  element.classList.add(`ids-theme-${segment}`);

  element.removeAttribute('data-theme');

  if (colorMode) {
    element.setAttribute('data-theme', colorMode);
  }

  if (!element.classList.contains('cdk-overlay-container')) {
    _elementWithSegment.next(element);
  }
};

/**
 * Envia novo valor de Segmento para os subscribers e para o elemento raiz da aplicação.
 *
 * @param value Novo valor de Segmento
 * @param elementToApplySegment elemento para aplicar o segmento
 * @param colorMode Modo de cor do segmento
 *
 * @category Segment
 *
 * @example
 * ```ts
 * import { changeSegment, Segments } from '@ids/tools';
 *
 * changeSegment(Segments.Uniclass);
 * ```
 */
export const changeSegment = async (
  segment = Segments.Varejo,
  elementToApplySegment: HTMLElement | Document | string = document,
  colorMode?: SegmentColorModeTypes,
) => {
  applySegmentToElement(segment, elementToApplySegment, colorMode);

  // A emissão do novo segmento foi passada para depois da chamada da applySegmentToElement
  // para que o attributo "data-theme" seja definido antes da próxima chamada
  // da função (overlay-container.ts:26)
  _segment.next(getKeySegment(segment));
};
